// src/pages/Login.js
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function GoogleIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}

const FEATURES = [
  'Communication & Collaboration',
  'Leadership Effectiveness',
  'Psychological Safety',
  'Goals & Role Clarity',
  'Morale & Wellbeing',
];

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { login, loginWithGoogle, currentUser } = useAuth();
  const navigate = useNavigate();

  // After a Google redirect on mobile, the page reloads and currentUser
  // gets set by onAuthStateChanged — navigate to dashboard automatically.
  useEffect(() => {
    if (currentUser) navigate('/dashboard');
  }, [currentUser, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(
        err.code === 'auth/invalid-credential'
          ? 'Invalid email or password.'
          : 'Failed to sign in. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      const result = await loginWithGoogle();
      // On mobile, loginWithGoogle() triggers a redirect and never returns —
      // navigation is handled by the useEffect above when the page reloads.
      // On desktop (popup flow), result is a credential object, so navigate now.
      if (result?.user) {
        navigate('/dashboard');
      }
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setError('Google sign-in failed. Please try again.');
      }
      setGoogleLoading(false);
    }
    // Note: don't call setGoogleLoading(false) here for redirect flow —
    // the page will navigate away anyway.
  };

  return (
    <div className="auth-layout">
      {/* Left hero panel */}
      <div className="auth-hero">
        <img src="/logo-white.png" alt="Meeting.Training" className="auth-hero-logo" />

        <h1>
          Understand<br />
          your team's<br />
          <em>heartbeat.</em>
        </h1>
        <p>
          Evidence-based assessments that reveal how your team truly works — and how to make it better.
        </p>

        <div className="auth-hero-features">
          {FEATURES.map((f) => (
            <div key={f} className="auth-hero-feature">
              <div className="auth-hero-feature-dot" />
              <span>{f}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div className="auth-form-panel">
        <div className="auth-form-box fade-up">
          <h2>Welcome back</h2>
          <p className="subtitle">Sign in to your account to continue</p>

          {error && <div className="alert alert-error">{error}</div>}

          <button
            onClick={handleGoogle}
            disabled={googleLoading}
            className="btn btn-secondary btn-full"
            style={{ marginBottom: 18 }}
          >
            {googleLoading ? <span className="spinner" /> : <GoogleIcon />}
            Continue with Google
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            <span style={{ fontSize: '0.72rem', color: 'var(--ink-muted)', whiteSpace: 'nowrap', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              or with email
            </span>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Email address</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
              />
            </div>
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <label>Password</label>
                <Link to="/forgot-password" className="link" style={{ fontSize: '0.8rem' }}>Forgot password?</Link>
              </div>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            <button type="submit" className="btn btn-primary btn-full" disabled={loading} style={{ marginTop: 4 }}>
              {loading ? <><span className="spinner" /> Signing in...</> : 'Sign in →'}
            </button>
          </form>

          <hr className="divider" />

          <p style={{ textAlign: 'center', fontSize: '0.85rem', color: 'var(--ink-muted)' }}>
            New here?{' '}
            <Link to="/pricing" className="link">View pricing</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
